import { ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";
import { DailyItemType, PlannerDatabaseItemSchema, PlannerDatabaseType, PlannerRecieverType, PlannerSaveResponseSchema, STATES, StateType, SummationDataType } from "../types/Planner";
import { extractSearchParams, removeReference } from "../helpers/JavascriptHelpers";
import { ApiCallType, GeneralObject, Interval, ServerResponse } from "../types/General";
import axios from "../axios";
import { ISOStringWithTimeZone, dayInMilliseconds } from "../helpers/DateAndTimeHelpers";
import useTestAndTimeSum from "../hooks/useTestAndTimeSum";
import NotificationContext from "./notification-context";
import { Mode } from "../components/notification/NotificationContainer";
import CacheApiContext from "./cache-api-context";
import useDecidePlanContacts from "../hooks/useDecidePlanReceiver";
import useApiCall from "../hooks/useApiCall";
import UserContext from "./user-context";

type PlannerContextType = {
    draftIndices: GeneralObject<GeneralObject<boolean>>;
    currentPlan: PlannerDatabaseType;
    state: StateType | null,
    summationData: SummationDataType;
    startDay: Date;
    endDay: Date;
    startSumDayLabel: string;
    page: number;
    submit: (data: PlannerDatabaseType, invalidateCache: boolean) => void;
    clearDraft: () => void;
    addInterval: (day: string) => void;
    updateDraft: (newItem: DailyItemType, index: number, givenDate?: string | null) => void;
    updateCurrentPlan: (newItems: PlannerDatabaseType) => void;
    listCopy: (data: (DailyItemType | null)[] | undefined | null) => void;
    singularCopy: (data: DailyItemType | undefined | null) => void;
    listPaste: (day: string) => void;
    singularPaste: (day: string, from?: number, to?: number, index?: number) => void;
    daysPaste: (newData: PlannerDatabaseType, pasteInterval: Interval | null, withoutNotification?: boolean) => void;
    deleteItem: (index: number, day?: string) => void;
    deleteDay: (day: string) => void;
    fetchOneDayPlan: (day: string) => void;
    refreshIntervalSum: (newIndex: number, newLabel: string) => void;
    loadMoreDays: () => void;
    cancelCopy: () => void;
    refresh: () => void;
}

type PlannerContextProviderProps = {
    children: ReactNode;
}

const PlannerContext = createContext<PlannerContextType | null>(null)

export function PlannerContextProvider({ children }: PlannerContextProviderProps) {
    const userContext = useContext(UserContext);
    const notificationContextProvider = useContext(NotificationContext);
    const cacheApiContext = useContext(CacheApiContext);

    const [page, setPage] = useState(2);
    const [startSumDayIndex, setStartSumDayIndex] = useState(0);
    const [startSumDayLabel, setStartSumDayLabel] = useState('هفته جاری')
    const [requestData, setRequestData] = useState<ApiCallType>({ url: '', key: '', fallbackData: null, schemaValidator: null });
    const [state, setState] = useState<StateType | null>(null);
    const [draftIndices, setDraftIndices] = useState<GeneralObject<GeneralObject<boolean>>>({})
    const [currentPlan, setCurrentPlan] = useState<PlannerDatabaseType>({});

    const { data, loading } = useApiCall(requestData);
    const contacts = useDecidePlanContacts();
    const summationData = useTestAndTimeSum(currentPlan, startSumDayIndex);
    
    const startDayRef = useRef(new Date(new Date().getTime() - (dayInMilliseconds * 2 * 7)));
    const endDayRef = useRef(new Date(new Date().getTime() - 1 + (dayInMilliseconds * 2 * 14)));
    const abortControllers = useRef<GeneralObject<AbortController | null>>({});
    const contactsRef = useRef<PlannerRecieverType | null>(null);
    const locallySavedPlan = useRef<GeneralObject<PlannerDatabaseType>>({});
    const locallySavedDraft = useRef<GeneralObject<GeneralObject<GeneralObject<boolean>>>>({});
    const listClipboard = useRef<(DailyItemType | null)[] | null>(null)
    const singularClipboard = useRef<DailyItemType | null>(null)

    useEffect(() => {
        if (!contacts || state === STATES[4]) return;

        contactsRef.current = contacts;
        const startDay = new Date(new Date().getTime() - (dayInMilliseconds * page * 7));
        const endDay = new Date(new Date().getTime() - 1 + (dayInMilliseconds * 2 * 14));

        const url = 'v2/planner-items?start_date=' + ISOStringWithTimeZone(startDay) 
                + '&end_date=' + ISOStringWithTimeZone(endDay) + '&user_ids=' + contacts.receiverId + '&role=' + contacts.receiverRole;

        if (contacts.receiverRole !== 1) {
            setRequestData({ url: url, key: contacts.receiverRole + '-' + contacts.receiverId, invalidateCache: true, 
                    fallbackData: null, schemaValidator: PlannerDatabaseItemSchema, refreshSingal: Date.now() })   
        }
    }, [contacts?.receiverId, contacts?.receiverRole, page])

    useEffect(() => {
        loading && setState(STATES[5]);
        !data && !loading && setState(STATES[6]);
        !loading && state !== STATES[4] && updateCurrentPlan(data as PlannerDatabaseType);
    }, [data, loading])

    function refresh() {
        setRequestData({...requestData, refreshSingal: (requestData?.refreshSingal || 0) + 1})
    }

    function refreshIntervalSum(newIndex: number, newLabel: string) {
        setStartSumDayIndex(newIndex);
        setStartSumDayLabel(newLabel);
    }

    function loadMoreDays() {
        setPage(prev => prev + 2);
    }

    function cancelCopy() {
        clearDraft();
    }

    function updateData(draftKey: string, draftIndices: GeneralObject<GeneralObject<boolean>>, plan: PlannerDatabaseType, 
                skipDraft: boolean = false, skipUpdating = false, skipSaving = false) {

        locallySavedDraft.current[draftKey] = {...draftIndices};
        locallySavedPlan.current[draftKey] = {...plan};

        try {
            if (!skipSaving && page <= 2) {
                window.localStorage.setItem('draft-indices', JSON.stringify(locallySavedDraft.current));
                window.localStorage.setItem('plan-draft', JSON.stringify(locallySavedPlan.current));
            }
        } catch (e) {
            window.localStorage.removeItem('plan-draft');
            window.localStorage.removeItem('draft-indices');
        }

        if (state !== STATES[4] && !skipUpdating)
            !skipDraft && Object.keys(draftIndices).length > 0 ? setState(STATES[1]) : setState(null)

        if (!skipUpdating) {
            setCurrentPlan({...plan});
            setDraftIndices({...draftIndices});
        }
    }

    function getData(customId: string | undefined = undefined, customRole: number | undefined = undefined) {
        const firstReceiver = customId || contacts?.receiverId.split(',')?.[0] || '';
        const draftKey = (customRole || contacts?.receiverRole || '2') + '-' + firstReceiver;

        let newCurrentPlan = null;
        if (state !== STATES[4]) {
            newCurrentPlan = {...(locallySavedPlan.current[draftKey] || {})};
        } else {
            const newData = {...(locallySavedPlan.current[draftKey] || {})};
            Object.keys(newData).forEach((day) => {
                const currentDayData = [...newData[day]];
                currentDayData.forEach((item, index) => {
                    if (!locallySavedDraft?.current?.[draftKey]?.[day]?.[index])
                        currentDayData[index] = null;
                })

                newData[day] = currentDayData;
            })

            newCurrentPlan = newData;
        }

        const newDraftIndices = {...(locallySavedDraft.current[draftKey] || {})};

        return { draftKey, newCurrentPlan, newDraftIndices }
    }

    function syncWithServer(newData: PlannerDatabaseType, id: string, role: number, skipSaving: boolean = false) {
        if (!contacts) return;

        const data = getData();
        const currentPlan = {...data.newCurrentPlan};
        const draftIndices = {...data.newDraftIndices};
        Object.keys(newData).forEach((date) => {
            currentPlan[date] = newData[date];
            delete draftIndices[date];
        })

        cacheApiContext?.setCache(contacts.receiverRole + '-' + contacts.receiverId, {...data.newCurrentPlan});
        contacts.receiverId.split(',').forEach((contact) => 
            cacheApiContext?.setCache(contacts.receiverRole + '-' + contact, {...data.newCurrentPlan}))

        updateData(data.draftKey, {...draftIndices}, {...currentPlan}, false, id !== contacts.receiverId || role !== contacts.receiverRole, skipSaving);
    }

    function mergePlans(externalPlan: PlannerDatabaseType, currentPlan: PlannerDatabaseType, draftIndices: GeneralObject<GeneralObject<boolean>>) {
        const mergedPlan = {} as PlannerDatabaseType;
        
        const allDays = {} as GeneralObject<boolean>;
        Object.keys(externalPlan).forEach((day) => { allDays[day] = true })
        Object.keys(currentPlan).forEach((day) => { allDays[day] = true })

        Object.keys(allDays).forEach((day) => {
            const externalPlanData = externalPlan[day] || [];
            const currentPlanData = currentPlan[day] || [];
            
            const maxLength = Math.max(externalPlanData.length, currentPlanData.length);
            const mergedData = [];
            mergedData[maxLength] = null;

            let lastNonEmptyIndex = 0;
            for (let i = 0; i < maxLength; i++) {
                mergedData[i] = draftIndices[day]?.[i] ? currentPlanData[i] : externalPlanData[i];
                if (mergedData[i]) lastNonEmptyIndex = i;
                
                if (currentPlanData[i] && externalPlanData[i] && currentPlanData[i]?.metadata?.lesson?.id && 
                            currentPlanData[i]?.metadata?.lesson?.id !== externalPlanData[i]?.metadata?.lesson.id) {

                    mergedData.push(externalPlanData[i]);
                    lastNonEmptyIndex = mergedData.length;
                }
            }

            const withoutEmptySuffix = [];
            for (let i = 0; i < mergedData.length; i++)
                if (i <= lastNonEmptyIndex) withoutEmptySuffix.push(mergedData[i]);

            mergedPlan[day] = withoutEmptySuffix;
        })

        return mergedPlan;
    }

    function clearDraft() {
        const urlData = extractSearchParams();
        const toClear = contacts?.receiverId.split(',') || [];
        urlData['copiedFrom'] && toClear.push(urlData['copiedFrom']);

        toClear.forEach((contact) => {
            delete locallySavedDraft.current[contacts?.receiverRole + '-' + contact];
            delete locallySavedPlan.current[contacts?.receiverRole + '-' + contact];
            cacheApiContext?.setCache(contacts?.receiverRole + '-' + contact, undefined);
        })

        window.localStorage.setItem('draft-indices', JSON.stringify(locallySavedDraft.current));
        window.localStorage.setItem('plan-draft', JSON.stringify(locallySavedPlan.current));

        if (contacts) {
            contactsRef.current = contacts;
            const startDay = new Date(new Date().getTime() - (dayInMilliseconds * page * 7));
            const endDay = new Date(new Date().getTime() - 1 + (dayInMilliseconds * 2 * 14));

            const url = 'v2/planner-items?start_date=' + ISOStringWithTimeZone(startDay) 
                    + '&end_date=' + ISOStringWithTimeZone(endDay) + '&user_ids=' + contacts.receiverId + '&role=' + contacts.receiverRole;

            setRequestData({ url: url, key: contacts.receiverRole + '-' + contacts.receiverId, invalidateCache: true, 
                    fallbackData: null, schemaValidator: PlannerDatabaseItemSchema, refreshSingal: (requestData.refreshSingal || 0) + 1})   
        }
    }

    function submit(data: PlannerDatabaseType, invalidateCache = false) {
        if (!contacts || !contactsRef.current) return;
        setState(STATES[0]);

        const aborterKey = getData().draftKey + '-' + Object.keys(data)[0];
        let abortController = abortControllers.current[aborterKey];
        abortController && abortController.abort();
        abortController = new AbortController();
        abortControllers.current[aborterKey] = abortController;

        axios.put('v2/planner-items', {user_ids: contacts.receiverId, role: contacts.receiverRole, data: data}, {signal: abortController.signal})
            .then((res) => {
                setState(null);
                userContext?.user?.is_demo && notificationContextProvider.addNotification({ content: 'دمو؛ اطلاعات شما ثبت نمی شود', type: Mode.Warning })
                
                notificationContextProvider.removeNotification('send-plan');
                const processedData = PlannerSaveResponseSchema.parse((res as ServerResponse).data);
                
                const reward = processedData.added_score;
                if (reward > 0) {
                    notificationContextProvider.addNotification({ content: '', type: Mode.Success, confetti: true });
                    userContext?.updateScore();
                }
                

                if (processedData.link) {
                    notificationContextProvider.addNotification({ 
                        timeout: -1,
                        content: 'کوئیز شما آمادست',
                        type: Mode.Error,
                        approveButtonContent: 'شرکت',
                        onApproveButtonClicked: () => processedData.link && window.location.replace(processedData.link)
                    });
                }

                abortControllers.current[aborterKey] = null;

                if (!userContext?.user?.is_demo)
                    invalidateCache ? clearDraft() : syncWithServer(processedData.data, contactsRef.current!.receiverId, contactsRef.current!.receiverRole)
            })
            .catch(() => setState(STATES[2]))
    }

    function addInterval(day: string) {
        const newCurrentPlan = {...currentPlan};
        const newData = newCurrentPlan[day] || [];
        
        if (newData.length > 4) {
            const currentDataLength = newData.length;
            newData[currentDataLength] = null;
            newCurrentPlan[day] = newData;

            setCurrentPlan(newCurrentPlan);
        }
    }

    function fetchOneDayPlan(day: string) {
        if (!currentPlan[day] && contacts) {
            const url = 'v2/planner-items?start_date=' + day + '&end_date=' + day 
                    + '&user_ids=' + contacts.receiverId + '&role=' + contacts.receiverRole;
                setState(STATES[5]);
                axios.get(url)
                    .then((res) => {
                        const processedData = PlannerDatabaseItemSchema.parse((res as ServerResponse).data);
                        syncWithServer(processedData, contactsRef.current!.receiverId, contactsRef.current!.receiverRole, true);
                    })
        }
    }

    function updateDraft(newItem: DailyItemType, index = -1, givenDate: string | null = null) {
        if (!contacts) return;

        const data = getData();
        const urlData = extractSearchParams();
        const date = givenDate || urlData['day'];

        if (date) {
            const newDraftData = data.newDraftIndices[date] || {};
            const newCurrentPlanData = data.newCurrentPlan[date] || [];
            const currentPlanLength = newCurrentPlanData.length;

            if (!newItem.role)
                newItem.role = contacts.submitOnChange ? contacts.receiverRole : 1;

            const editIndex = Number(index);
            if (!isNaN(editIndex) && index !== -1) {
                newDraftData[editIndex] = true;                
                newCurrentPlanData[editIndex] = newItem;
            } else {
                newDraftData[currentPlanLength] = true;
                newCurrentPlanData.push(newItem);
            }

            data.newCurrentPlan[date] = newCurrentPlanData;
            data.newDraftIndices[date] = newDraftData;

            state !== STATES[4] && submit({[date]: {...data.newCurrentPlan[date]}});

            const selectedDate = new Date(date);
            updateData(data.draftKey, data.newDraftIndices, data.newCurrentPlan, true, false, 
                    selectedDate > endDayRef.current || selectedDate < startDayRef.current);
        }
    }

    function updateCurrentPlan(newData: PlannerDatabaseType) {
        if (!contacts || !newData) return;

        const data = getData();
        const mergedPlan = mergePlans(newData, data.newCurrentPlan, data.newDraftIndices);
        updateData(data.draftKey, data.newDraftIndices, mergedPlan);
    }

    function singularCopy(data: DailyItemType | undefined | null) {
        notificationContextProvider.addNotification({ content: 'کپی شد', type: Mode.Success, timeout: 1000 });

        if (data)
            singularClipboard.current = data;
    }

    function listCopy(data: (DailyItemType | null)[] | undefined | null) {
        notificationContextProvider.addNotification({ content: 'کپی شد', type: Mode.Success, timeout: 1000 });

        if (data)
            listClipboard.current = data;
    }

    function daysPaste(newData: PlannerDatabaseType, interval: Interval | null, withoutNotification = false) {
        if (!contacts) return;

        const data = getData();
        let currentDate = interval?.start || new Date();

        const newCurrentPlan = {} as PlannerDatabaseType;
        const newDraftIndices = {} as GeneralObject<GeneralObject<boolean>>;
        Object.keys(newData).forEach((day) => {
            const data = newData[day];

            const todayDraftIndices = {} as GeneralObject<boolean>;
            const todayPlan = [] as (DailyItemType | null)[];
            for (let i = 0; i < data.length; i++) {
                if (data[i]) {
                    todayPlan[i] = data[i];
                    todayDraftIndices[i] = true;
                }
            }

            newCurrentPlan[ISOStringWithTimeZone(currentDate)] = todayPlan;
            newDraftIndices[ISOStringWithTimeZone(currentDate)] = todayDraftIndices;

            currentDate = new Date(currentDate.getTime() + dayInMilliseconds);
        })

        !withoutNotification  && notificationContextProvider.addNotification({ content: 'پیست شد', type: Mode.Success, timeout: 1000 });
        updateData(data.draftKey, newDraftIndices, newCurrentPlan, true);
        setState(STATES[4]);
    }

    function listPaste(day: string) {
        if (listClipboard.current && contacts) {
            const data = getData();

            const removedReferenceClipboard = [] as (DailyItemType | null)[];
            const newDraftData = {} as GeneralObject<boolean>;
            for (let i = 0; i < listClipboard.current.length; i++) {
                const item = listClipboard.current[i];
                removedReferenceClipboard.push(item);
                
                if (item)
                    newDraftData[i] = true; 
            }
            data.newCurrentPlan[day] = Object.values(removedReferenceClipboard);
            data.newDraftIndices[day] = newDraftData;

            notificationContextProvider.addNotification({ content: 'پیست شد', type: Mode.Success, timeout: 1000 });
            state !== STATES[4] && submit({[day]: Object.values(removedReferenceClipboard)});
            updateData(data.draftKey, data.newDraftIndices, data.newCurrentPlan, true);
        }
    }

    function singularPaste(day: string, from = 0, to = 0, index = -1) {
        if (singularClipboard.current && contacts) {
            const data = getData();

            const newDraftData = data.newDraftIndices[day] || {};
            const newPlanData = data.newCurrentPlan[day] || [];

            const toCopy = removeReference(singularClipboard.current);
            if (from && to) {
                toCopy.from = from;
                toCopy.to = to;
            }

            if (index === -1) {
                newPlanData.push(toCopy);
                newDraftData[currentPlan[day]?.length || 0] = true;
            } else {
                newPlanData[index] = toCopy;
                newDraftData[index] = true;
            }

            data.newDraftIndices[day] = newDraftData;
            data.newCurrentPlan[day] = newPlanData;

            notificationContextProvider.addNotification({ content: 'پیست شد', type: Mode.Success, timeout: 1000 });
            state !== STATES[4] && submit({[day]: newPlanData});
            updateData(data.draftKey, data.newDraftIndices, data.newCurrentPlan, true);
        }
    }

    function deleteItem(index: number, day?: string) {
        if (!contacts) return;

        const data = getData();
        const urlData = extractSearchParams();
        const selectedDay = day || urlData['day'];
        
        if (selectedDay) {
            const newDraftData = data.newDraftIndices[selectedDay] || {};
            const newPlanData = data.newCurrentPlan[selectedDay] || [];

            delete newDraftData[index];
            newPlanData[index] = null;

            data.newCurrentPlan[selectedDay] = newPlanData;
            data.newDraftIndices[selectedDay] = newDraftData;

            if (Object.keys(newDraftData).length === 0)
                delete data.newDraftIndices[selectedDay];

            notificationContextProvider.addNotification({ content: 'حذف شد', type: Mode.Success, timeout: 1000 });
            state !== STATES[4] && submit({[selectedDay]: newPlanData});
            updateData(data.draftKey, data.newDraftIndices, data.newCurrentPlan);
        }
    }

    function deleteDay(day: string) {
        const data = getData();

        const newCurrentPlan = {...currentPlan};
        const currentDayPlan = newCurrentPlan[day] || [];
        const newDayPlan = [] as (DailyItemType | null)[];

        currentDayPlan.forEach((item, index) => {
            if (item?.role === 1)
                newDayPlan.push(null);
            else
                newDayPlan.push(item);
        })

        state !== STATES[4] && submit({[day]: newDayPlan});
        updateData(data.draftKey, data.newDraftIndices, data.newCurrentPlan);
    }

    useEffect(() => {
        const savedPlan = window.localStorage.getItem('plan-draft') || "{}";
        const savedDraftIndices = window.localStorage.getItem('draft-indices') || "{}";
        const startSumDayIndex = Number(window.localStorage.getItem('startSumDayIndex') || 0);
        const startSumDayLabel = window.localStorage.getItem('startSumDayLabel') || 'هفته جاری';

        const firstReceiver = contacts?.receiverId?.split(',')?.[0] || '';
        const draftKey = (contacts?.receiverRole || '2') + '-' + firstReceiver;

        const processedPlan = JSON.parse(savedPlan) as GeneralObject<PlannerDatabaseType>;
        const processedDraftIndices = JSON.parse(savedDraftIndices) as GeneralObject<GeneralObject<GeneralObject<boolean>>>;

        locallySavedDraft.current = processedDraftIndices;
        locallySavedPlan.current = processedPlan;
        updateCurrentPlan(processedPlan[draftKey] || {});
        setStartSumDayIndex(startSumDayIndex);
        setStartSumDayLabel(startSumDayLabel);
    }, [])

    const context = {
        draftIndices,
        currentPlan,
        state,
        summationData,
        startDay: startDayRef.current,
        endDay: endDayRef.current,
        startSumDayLabel,
        page,
        submit,
        clearDraft,
        addInterval,
        updateDraft,
        updateCurrentPlan,
        singularCopy,
        listCopy,
        listPaste,
        singularPaste,
        daysPaste,
        deleteItem,
        deleteDay,
        fetchOneDayPlan,
        refreshIntervalSum,
        loadMoreDays,
        cancelCopy,
        refresh
    }

    return (
        <PlannerContext.Provider value={context}>
            {children}
        </PlannerContext.Provider>
    )
}

export default PlannerContext;